
  import { computed, defineComponent, ref, watch, inject, Ref } from 'vue';
  import { Loader } from '@/ui/components/Loader';
  import { Box } from '@/ui/components/Box';
  import { InputField } from '@/ui/components/form/InputField';
  import { ResourceTable } from '@/ui/components/table/ResourceTable';
  import { du } from '@/ui/i18n/i18n';
  import { Tag } from '@/ui/components/Tag';
  import { toFixed } from '@/ui/helpers/numberHelper';
  import { useDetailedView, useDetailedViewFilters } from '@/ui/concerns/analytics/useDetailedView';
  import { useDetailedViewStore } from '@/stores/analytics/detailedView';
  import { ReportRecordData } from '@/services/api/resources/analytics-builder/model';
  import { reportFromAnalyticsPageKey } from '@/ui/injectionKeys';
  import StaticPagination from '@/ui/components/StaticPagination.vue';
  import { openNewRouterTab } from '@/ui/helpers';
  import { useApprovalStatusesStore } from '@/stores/analytics/approvalStatuses';
  import { approvalStatusTagClass } from '@/ui/helpers/approvalsHelper';
  import { approvalsEnabledForOrganization } from '@/ui/auth/auth';

  export default defineComponent({
    components: {
      Loader,
      Box,
      InputField,
      ResourceTable,
      Tag,
      Pagination: StaticPagination,
    },
    setup() {
      const { getDetailedViewDataTask, data } = useDetailedView();
      const { prepareFilters } = useDetailedViewFilters();
      const approvalStatusesStore = useApprovalStatusesStore();
      const store = useDetailedViewStore();
      const report = inject<Ref<ReportRecordData | null>>(reportFromAnalyticsPageKey, ref(null));

      const search = ref('');
      const page = ref(1);
      const perPage = 20;

      const widget = computed(() => report.value?.widgets.find(widget => widget.key === store.widgetKey));
      const combinedFilters = computed(() => prepareFilters(
        widget.value!.filters,
        report.value!.filters,
        widget.value!.attribute,
        store.resourceId!,
        store.options,
      ));
      const taskParams = computed(() => ({
        filters: combinedFilters.value,
        metric: widget.value!.metric,
        attribute: widget.value!.attribute,
        page: page.value,
        perPage,
        searchTerm: search.value,
      }));

      const onPageChanged = (value: number) => {
        page.value = value;
      };

      const updateSearch = (value: string) => {
        search.value = value;
      };

      watch([page, search], () => getDetailedViewDataTask.value.run(taskParams.value));

      const onRowClick = ({ id }: { id: string }) => openNewRouterTab({ name: 'inspectionDetail', params: { id } });

      const getStatus = (status: number) => approvalStatusesStore.find(status)?.title;

      const getApprovalStatusTagClass = (status: number) => {
        const originalTitle = approvalStatusesStore.find(status)?.originalTitle ?? '';
        return approvalStatusTagClass(originalTitle as never);
      };

      return {
        search,
        page,
        perPage,
        onPageChanged,
        updateSearch,
        getDetailedViewDataTask,
        taskParams,
        data,
        attribute: widget.value?.attribute,
        onRowClick,
        approvalStatusesStore,
        approvalsEnabledForOrganization,
        getStatus,
        getApprovalStatusTagClass,
      };
    },
    methods: {
      toFixed,
      du,
    },
  });
